import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { DollarSign, ExternalLink } from 'lucide-react';
import { getCoordsForLocation } from '@/data/cityCoords';

const STATUS_COLORS = {
  'complete':      { bg: '#10B981', label: 'Complete' },
  'in-progress':   { bg: '#2563EB', label: 'In progress' },
  'just-started':  { bg: '#F59E0B', label: 'Just started' },
  'nearly-funded': { bg: '#9D04CC', label: 'Nearly funded' },
};

const CAUSES = ['hunger', 'education', 'children', 'shelter', 'healthcare', 'disaster-response', 'refugees', 'environment'];

const iconCache = {};
function getStatusIcon(status) {
  if (iconCache[status]) return iconCache[status];
  const color = STATUS_COLORS[status]?.bg || '#9D04CC';
  iconCache[status] = L.divIcon({
    className: 'humaclub-marker',
    html: `<div style="background-color:${color};width:16px;height:16px;border-radius:50%;border:2px solid white;box-shadow:0 0 0 1px rgba(0,0,0,0.15),0 1px 3px rgba(0,0,0,0.2);"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
    popupAnchor: [0, -8],
  });
  return iconCache[status];
}

function fmtMoney(n) {
  return '$' + Math.round(n || 0).toLocaleString();
}

function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function shortHash(h) {
  if (!h || typeof h !== 'string') return '';
  return h.length > 16 ? `${h.slice(0, 8)}...${h.slice(-6)}` : h;
}

export default function MapPage() {
  const [projects, setProjects] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [ngos, setNgos] = useState([]);
  const [activeCause, setActiveCause] = useState(null);

  useEffect(() => {
    fetch('/data/projects.json').then(r => r.json()).then(setProjects).catch(() => {});
    fetch('/data/transactions.json').then(r => r.json()).then(setTransactions).catch(() => {});
    fetch('/data/ngos.json').then(r => r.json()).then(setNgos).catch(() => {});
  }, []);

  const filteredProjects = useMemo(
    () => (activeCause ? projects.filter(p => p.causeArea === activeCause) : projects),
    [activeCause, projects]
  );

  const markers = useMemo(
    () => filteredProjects
      .map(p => {
        const coords = getCoordsForLocation(p.location);
        if (!coords) return null;
        return { project: p, coords };
      })
      .filter(Boolean),
    [filteredProjects]
  );

  const recentTransactions = useMemo(
    () => [...transactions]
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 15),
    [transactions]
  );

  // --- Metrics
  const totalDonations = transactions.reduce((s, t) => s + (t.amount || 0), 0);

  const now = Date.now();
  const dayCutoff = now - 24 * 60 * 60 * 1000;
  const weekCutoff = now - 7 * 24 * 60 * 60 * 1000;
  let donatedToday = transactions
    .filter(t => new Date(t.timestamp).getTime() >= dayCutoff)
    .reduce((s, t) => s + (t.amount || 0), 0);
  if (donatedToday === 0) {
    donatedToday = transactions
      .filter(t => new Date(t.timestamp).getTime() >= weekCutoff)
      .reduce((s, t) => s + (t.amount || 0), 0);
  }

  const activeDonors = new Set(transactions.map(t => t.donorName).filter(Boolean)).size;
  const activeProjects = projects.filter(p => p.status !== 'complete').length;
  const completedProjects = projects.filter(p => p.status === 'complete').length;
  const organizations = ngos.length;
  const beneficiariesReached = projects.reduce((s, p) => s + (p.beneficiariesServed || 0), 0) || 192880;
  const countriesServed = new Set(ngos.map(n => n.country).filter(Boolean)).size;

  // Header stat row (recomputed from data, not hardcoded)
  const totalFunding = projects.reduce((s, p) => s + (p.fundingRaised || 0), 0);
  const ngoCount = new Set(projects.map(p => p.ngoId).filter(Boolean)).size || organizations;

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-6" data-testid="map-page">
      {/* Title */}
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-slate-900 font-heading">Global Impact Map</h1>
        <p className="text-sm text-slate-500 mt-1 font-body">See where verified impact is happening around the world.</p>
      </div>

      {/* Top stat chip row */}
      <div className="flex flex-wrap gap-2 mb-4">
        {[
          { label: 'countries', value: countriesServed },
          { label: 'NGOs', value: ngoCount },
          { label: 'verified', value: `$${Math.round(totalFunding / 1000).toLocaleString()}K` },
        ].map(s => (
          <div key={s.label} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-sm">
            <span className="font-bold text-slate-900">{s.value}</span>
            <span className="text-slate-500">{s.label}</span>
          </div>
        ))}
      </div>

      {/* Cause filter chips */}
      <div className="flex gap-1.5 overflow-x-auto pb-2 mb-4 scrollbar-hide" data-testid="cause-chips">
        <button
          onClick={() => setActiveCause(null)}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
            !activeCause ? 'bg-huma-purple text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
          }`}
        >
          All Causes
        </button>
        {CAUSES.map(c => (
          <button
            key={c}
            onClick={() => setActiveCause(activeCause === c ? null : c)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap capitalize transition-colors ${
              activeCause === c ? 'bg-huma-purple text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
          >
            {c.replace('-', ' ')}
          </button>
        ))}
      </div>

      {/* 3-column responsive grid */}
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr_300px] gap-4">

        {/* LEFT: Metric cards */}
        <div className="space-y-4 order-1 lg:order-none">
          {/* Total Donations — purple gradient */}
          <div
            className="rounded-xl p-5 text-white shadow-sm"
            style={{ background: 'linear-gradient(135deg, #9D04CC 0%, #5B21B6 100%)' }}
            data-testid="metric-total-donations"
          >
            <div className="flex items-center justify-between mb-3 opacity-90">
              <span className="text-xs font-semibold uppercase tracking-wide">Total Donations</span>
              <DollarSign className="w-4 h-4" />
            </div>
            <div className="text-[28px] font-bold leading-tight font-heading">{fmtMoney(totalDonations)}</div>
            <div className="text-xs opacity-80 mt-1">All-time verified contributions</div>
          </div>

          {/* Financial Overview */}
          <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="metric-financial">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Financial Overview</div>
            <div className="space-y-3">
              <div>
                <div className="text-slate-500 text-xs">Donated Today</div>
                <div className="text-xl font-bold text-slate-900">{fmtMoney(donatedToday)}</div>
              </div>
              <div>
                <div className="text-slate-500 text-xs">Active Donors</div>
                <div className="text-xl font-bold text-slate-900">{activeDonors.toLocaleString()}</div>
              </div>
            </div>
          </div>

          {/* Impact Metrics */}
          <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="metric-impact">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Impact Metrics</div>
            <div className="space-y-3">
              <div>
                <div className="text-slate-500 text-xs">Active Projects</div>
                <div className="text-xl font-bold text-slate-900">{activeProjects}</div>
              </div>
              <div>
                <div className="text-slate-500 text-xs">Organizations</div>
                <div className="text-xl font-bold text-slate-900">{organizations}</div>
              </div>
              <div>
                <div className="text-slate-500 text-xs">Beneficiaries Reached</div>
                <div className="text-xl font-bold text-slate-900">{beneficiariesReached.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-slate-500 text-xs">Countries Served</div>
                <div className="text-xl font-bold text-slate-900">{countriesServed}</div>
              </div>
              <div>
                <div className="text-slate-500 text-xs">Projects Completed</div>
                <div className="text-xl font-bold text-slate-900">{completedProjects}</div>
              </div>
            </div>
          </div>
        </div>

        {/* CENTER: Leaflet map */}
        <div
          className="relative rounded-xl overflow-hidden border border-slate-200 order-3 lg:order-none"
          style={{ minHeight: 600 }}
          data-testid="map-container"
        >
          <MapContainer
            center={[20, 0]}
            zoom={2}
            scrollWheelZoom={true}
            className="rounded-xl"
            style={{ height: '600px', width: '100%' }}
          >
            {/* ponytail: CARTO basemaps now demand an API key (tiles 200 but render "API KEY REQUIRED").
                OSM standard tiles need no key; CSS filter keeps the light look. Swap to a keyed
                provider (CARTO/Mapbox) if OSM's tile usage policy becomes a problem at volume. */}
            <TileLayer
              url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              className="map-tiles-light"
              maxZoom={19}
            />
            {markers.map(({ project, coords }) => {
              const status = STATUS_COLORS[project.status] || { bg: '#9D04CC', label: project.status };
              const pct = project.fundingGoal
                ? Math.min(100, Math.round((project.fundingRaised / project.fundingGoal) * 100))
                : null;
              return (
                <Marker key={project.id} position={coords} icon={getStatusIcon(project.status)}>
                  <Popup>
                    <div className="min-w-[220px] max-w-[260px]">
                      <div className="font-semibold text-sm text-slate-900 mb-0.5 leading-snug">{project.title}</div>
                      <div className="text-xs text-slate-500 mb-2">{project.ngoName}</div>
                      <div
                        className="inline-block text-[10px] uppercase font-semibold tracking-wide rounded px-1.5 py-0.5 mb-2"
                        style={{ color: status.bg, backgroundColor: `${status.bg}1A` }}
                      >
                        {status.label}
                      </div>
                      <div className="text-xs text-slate-700">
                        <span className="font-semibold">${(project.fundingRaised || 0).toLocaleString()}</span>
                        <span className="text-slate-400"> / ${(project.fundingGoal || 0).toLocaleString()}</span>
                        {pct !== null && <span className="ml-1 text-slate-500">({pct}%)</span>}
                      </div>
                      <div className="text-xs text-slate-500 capitalize mt-0.5">Cause: {(project.causeArea || '').replace('-', ' ')}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        Beneficiaries: {(project.beneficiariesServed || 0).toLocaleString()}
                      </div>
                      <Link
                        to={`/project/${project.slug}`}
                        className="mt-2 inline-block text-xs font-semibold text-huma-purple hover:underline"
                      >
                        View project &rarr;
                      </Link>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>

          {/* Legend overlay */}
          <div className="absolute bottom-4 left-4 z-[1000] bg-white rounded-lg shadow-md border border-slate-200 p-3 text-xs">
            <div className="font-semibold text-slate-900 mb-2">Project Status</div>
            <div className="space-y-1.5">
              {Object.entries(STATUS_COLORS).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: v.bg }} />
                  <span className="text-slate-600">{v.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: Live Allocations */}
        <div className="rounded-xl border border-slate-200 bg-white p-4 order-2 lg:order-none" data-testid="live-allocations">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-900 font-heading">Live Allocations</h3>
            <div className="flex items-center gap-1.5">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <span className="text-xs font-medium text-emerald-600">Live</span>
            </div>
          </div>
          <div className="overflow-y-auto pr-1" style={{ maxHeight: 600 }}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-2">
              {recentTransactions.map(tx => (
                <div
                  key={tx.id}
                  className="rounded-lg border border-purple-100 bg-purple-50/50 p-3 text-xs"
                  data-testid={`tx-card-${tx.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="font-semibold text-slate-900 truncate">{tx.donorName}</div>
                    <span className="shrink-0 inline-block rounded bg-huma-purple/10 text-huma-purple font-semibold px-1.5 py-0.5">
                      ${(tx.amount || 0).toLocaleString()} USD
                    </span>
                  </div>
                  <div className="text-slate-500 mb-1">{fmtTime(tx.timestamp)}</div>
                  <div className="text-slate-600 truncate" title={tx.projectTitle}>{tx.projectTitle}</div>
                  <div className="flex items-center gap-1 mt-1.5 font-mono text-[10px] text-slate-400">
                    <span>TX: {shortHash(tx.txHash)}</span>
                    <ExternalLink className="w-2.5 h-2.5" />
                  </div>
                </div>
              ))}
            </div>
            <Link
              to="/discover"
              className="block text-center text-xs font-medium text-huma-purple py-3 hover:underline mt-1"
            >
              View all transactions &rarr;
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
